import { useEffect, useState } from 'react';
import { api, type DentistMonthly, type DentistStatement } from '../api';
import { useI18n } from '../i18n';
import { EmptyState, ErrorState, Field, Modal, Skeleton, Spinner, Tabs } from '../components';
import { formatMinor, minorToInput, parseToMinor } from '../money';
import { OFFERED_PAYMENT_METHODS } from '@oxyflows/contracts';
import { initials } from '../App';
import { describe } from '../errors';

/**
 * Dentists, and what the clinic owes them (spec §11).
 *
 * Three things this page is careful about.
 *
 * A DENTIST IS NOT ALWAYS ON COMMISSION. Salary and commission are separate
 * agreements that run side by side, and the statement keeps them apart: a
 * salaried month is a posted amount for that month, a commission is earned
 * against collections, and adding them into one "earnings" figure would hide
 * which is which.
 *
 * A PAYMENT IS AN AMOUNT, NOT A PERIOD. A dentist asks for half, or a round two
 * hundred. The clinic names the figure and it pays off the oldest earnings
 * first, leaving part-paid earnings visibly part-paid.
 *
 * COUNTS ARE NOT INTERCHANGEABLE. Unique patients, unique teeth and procedures
 * are three different numbers and are never presented as one.
 */
export function Dentists({
  clinicId,
  permissions,
  ownMembershipId,
}: {
  clinicId: string;
  permissions: string[];
  ownMembershipId: string | null;
}) {
  const { t, formatNumber } = useI18n();
  const [rows, setRows] = useState<Awaited<ReturnType<typeof api.dentists>>['dentists'] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);
  const [adding, setAdding] = useState(false);
  const [hoursFor, setHoursFor] = useState<{ id: string; name: string } | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const canManage = permissions.includes('dentist.manage');
  const canSchedule = canManage || permissions.includes('schedule.manage');

  const canSeeAll = permissions.includes('commission.read.all');
  const canSeeOwn = permissions.includes('commission.read.own');

  useEffect(() => {
    setRows(null);
    api
      .dentists()
      .then((r) => setRows(r.dentists))
      .catch((e) => setError(describe(e, t)));
  }, [clinicId, nonce, t]);

  if (error) return <ErrorState message={error} onRetry={() => setNonce((n) => n + 1)} />;

  return (
    <>
      <div className="page-head">
        <div>
          <h1>{t.dentists.title}</h1>
          {!canSeeAll && canSeeOwn ? <div className="page-sub">{t.dentists.ownOnly}</div> : null}
        </div>
        {canManage ? (
          <button type="button" className="btn" onClick={() => setAdding(true)}>
            + {t.dentists.add}
          </button>
        ) : null}
      </div>
      {notice ? (
        <div className="notice ok" role="status" style={{ marginBottom: 16 }}>
          <span>{notice}</span>
        </div>
      ) : null}

      <div className="card">
        {rows === null ? (
          <Skeleton rows={4} />
        ) : rows.length === 0 ? (
          <EmptyState title={t.dentists.noDentists} />
        ) : (
          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <th>{t.inventory.name}</th>
                  <th>{t.dentists.assignedPatients}</th>
                  <th>{t.dentists.completedProcedures}</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {rows.map((d) => {
                  const viewable = canSeeAll || (canSeeOwn && d.membershipId === ownMembershipId);
                  return (
                    <tr key={d.membershipId}>
                      <td>
                        <div className="row">
                          <span className="avatar">{initials(d.name)}</span>
                          <div>
                            <strong>{d.name}</strong>
                            {d.email ? <span className="sub ltr-value">{d.email}</span> : <span className="sub">{t.dentists.noSignIn}</span>}
                          </div>
                        </div>
                      </td>
                      <td>{formatNumber(d.assignedPatients)}</td>
                      <td>{formatNumber(d.completedProcedures)}</td>
                      <td style={{ textAlign: 'end', whiteSpace: 'nowrap' }}>
                        {canSchedule ? (
                          <button type="button" className="btn-link" style={{ marginInlineEnd: 16 }} onClick={() => setHoursFor({ id: d.membershipId, name: d.name })}>
                            {t.dentists.hours}
                          </button>
                        ) : null}
                        <button
                          type="button"
                          className="btn-link"
                          onClick={() => setOpenId(d.membershipId)}
                          disabled={!viewable}
                          title={viewable ? undefined : t.dentists.ownOnly}
                        >
                          {t.dentists.statement}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {adding ? (
        <AddDentist
          onClose={() => setAdding(false)}
          onAdded={() => {
            setAdding(false);
            setNotice(t.dentists.added);
            setNonce((n) => n + 1);
          }}
        />
      ) : null}
      {hoursFor ? (
        <WorkingHours
          membershipId={hoursFor.id}
          name={hoursFor.name}
          onClose={() => setHoursFor(null)}
          onSaved={() => {
            setHoursFor(null);
            setNotice(t.dentists.hoursSaved);
          }}
        />
      ) : null}
      {openId ? (
        <StatementPanel
          membershipId={openId}
          canManage={permissions.includes('dentist.manage')}
          canSettle={permissions.includes('commission.settle')}
          onClose={() => setOpenId(null)}
          onChanged={() => setNonce((n) => n + 1)}
        />
      ) : null}
    </>
  );
}

function StatementPanel({
  membershipId,
  canManage,
  canSettle,
  onClose,
  onChanged,
}: {
  membershipId: string;
  canManage: boolean;
  canSettle: boolean;
  onClose: () => void;
  onChanged: () => void;
}) {
  const { t, locale, formatDate, formatNumber, tx } = useI18n();
  const [data, setData] = useState<DentistStatement | null>(null);
  const [monthly, setMonthly] = useState<DentistMonthly | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busyMonth, setBusyMonth] = useState<string | null>(null);
  const [tab, setTab] = useState('summary');
  const [dialog, setDialog] = useState<'agreement' | 'pay' | null>(null);
  const [reversing, setReversing] = useState<DentistStatement['settlements'][number] | null>(null);
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    api.dentistStatement(membershipId).then(setData).catch((e) => setError(describe(e, t)));
    api.dentistMonthly(membershipId, 12).then(setMonthly).catch(() => {
      /* The monthly view is supplementary; the statement still stands without it. */
    });
  }, [membershipId, nonce, t]);

  async function postSalary(month: string) {
    setBusyMonth(month);
    setError(null);
    try {
      await api.postSalary(membershipId, month);
      setNonce((n) => n + 1);
      onChanged();
    } catch (e) {
      setError(describe(e, t));
    } finally {
      setBusyMonth(null);
    }
  }

  const salaryOnly = data ? data.salary.monthlyMinor > 0 && data.commission.basis === null : false;
  const both = data ? data.salary.monthlyMinor > 0 && data.commission.basis !== null : false;
  // The owner: paid from profit, not per procedure. The production figures
  // below are still shown — they are what the profit came from.
  const isOwner = data ? data.owner !== null : false;

  return (
    <Modal
      title={data ? `${t.dentists.statement} — ${data.dentist.name}` : t.dentists.statement}
      onClose={onClose}
      footer={
        data ? (
          <>
            {canManage ? (
              <button type="button" className="btn btn-ghost" onClick={() => setDialog('agreement')}>
                {t.dentists.newAgreement}
              </button>
            ) : null}
            {canSettle ? (
              <button type="button" className="btn" onClick={() => setDialog('pay')}>
                {isOwner ? t.dentists.takeDraw : t.dentists.payDentist}
              </button>
            ) : null}
          </>
        ) : null
      }
    >
      {!data ? (
        <Skeleton rows={6} />
      ) : (
        <div className="stack" style={{ gap: 16 }}>
          {error ? (
            <div className="notice danger" role="alert">
              {error}
            </div>
          ) : null}

          {/*
            How this person is paid, stated once, at the top — and what the
            basis actually MEANS, because "on collections" and "on billed" look
            similar and differ entirely in who carries the loss when a patient
            stops paying. That is the question, and a two-word label does not
            answer it.
          */}
          <div className="pay-basis">
            <div className="row" style={{ flexWrap: 'wrap' }}>
              <span className="section-label" style={{ margin: 0 }}>
                {t.dentists.payKind}
              </span>
              <span className="badge brand">
                {isOwner
                  ? (t.dentists.basisKind as Record<string, string>).profit
                  : both
                    ? t.dentists.salaryAndCommission
                    : salaryOnly
                      ? t.dentists.salaryOnly
                      : data.commission.basis
                        ? t.dentists.commissionOnly
                        : '—'}
              </span>
              {data.salary.monthlyMinor > 0 ? (
                <span className="badge">
                  {formatMinor(data.salary.monthlyMinor, data.currency, locale)} {t.dentists.perMonth}
                </span>
              ) : null}
              {data.commission.rateBp !== null && data.commission.basis ? (
                <span className="badge">
                  {(data.commission.rateBp / 100).toFixed(2)}% ·{' '}
                  {(t.dentists.basisKind as Record<string, string>)[data.commission.basis] ?? data.commission.basis}
                </span>
              ) : null}
            </div>

            {data.commission.basis && !isOwner ? (
              <p>
                {(t.dentists.basisExplainer as Record<string, string>)[data.commission.basis] ?? ''}
              </p>
            ) : null}
            {data.salary.monthlyMinor > 0 ? (
              <p>{(t.dentists.basisExplainer as Record<string, string>).salary}</p>
            ) : null}

            {data.commission.basis && !isOwner ? (
              <div className="row" style={{ gap: 8, marginTop: 4 }}>
                <span className="section-label" style={{ margin: 0 }}>
                  {t.dentists.whoCarriesRisk}
                </span>
                <span className={data.commission.basis === 'billed' ? 'badge warn' : 'badge ok'}>
                  {data.commission.basis === 'billed' ? t.dentists.riskClinic : t.dentists.riskShared}
                </span>
              </div>
            ) : null}
          </div>

          {/* Salary that is owed but not posted. Never posted on a read: a page
              load must not create a financial record. */}
          {data.salary.unposted.length > 0 && canManage ? (
            <div className="notice warn">
              <div style={{ flex: 1 }}>
                <strong>{t.dentists.unpostedSalary}</strong>
                <div style={{ marginTop: 4, fontSize: 12.5 }}>{t.dentists.unpostedSalaryNote}</div>
                <div className="stack" style={{ gap: 6, marginTop: 10 }}>
                  {data.salary.unposted.map((m) => (
                    <div key={m.month} className="row" style={{ justifyContent: 'space-between' }}>
                      <span>
                        <strong className="ltr-value">{m.month}</strong>{' '}
                        <span className="ltr-value">{formatMinor(m.amountMinor, data.currency, locale)}</span>
                        {m.coveredDays !== m.monthDays ? (
                          <span style={{ fontSize: 12 }}>
                            {' · '}
                            {tx(t.dentists.proRated, { days: m.coveredDays, of: m.monthDays })}
                          </span>
                        ) : null}
                      </span>
                      <button
                        type="button"
                        className="btn btn-sm"
                        disabled={busyMonth === m.month}
                        onClick={() => void postSalary(m.month)}
                      >
                        {busyMonth === m.month ? <Spinner /> : t.dentists.postSalary}
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ) : null}

          <Tabs
            value={tab}
            onChange={setTab}
            tabs={[
              { value: 'summary', label: t.dentists.statement },
              { value: 'monthly', label: t.dentists.monthly },
              { value: 'agreements', label: t.dentists.agreements },
              { value: 'payments', label: t.dentists.payments },
            ]}
          />

          {tab === 'summary' ? (
            <>
              <div>
                <div className="section-label">{t.dentists.production}</div>
                <div className="grid-2" style={{ marginTop: 8, gap: 10 }}>
                  <Metric label={t.finance.uniquePatients} value={formatNumber(data.production.uniquePatients)} />
                  <Metric label={t.finance.uniqueTeeth} value={formatNumber(data.production.uniqueTeeth)} />
                  <Metric label={t.finance.procedures} value={formatNumber(data.production.procedures)} />
                  <Metric
                    label={t.finance.produced}
                    value={formatMinor(data.production.producedMinor, data.currency, locale)}
                  />
                </div>
              </div>

              {/* Said before the numbers, not after: for a salaried dentist
                  the production figures are context, not the basis of pay. */}
              {salaryOnly ? (
                <div className="notice">
                  <span>{t.dentists.salaryPayNote}</span>
                </div>
              ) : null}

              {isOwner && data.owner ? (
                <div>
                  <div className="section-label">{t.dentists.ownerTitle}</div>
                  <p style={{ color: 'var(--ink-3)', fontSize: 12.5, margin: '6px 0 8px' }}>{t.dentists.ownerHint}</p>
                  <div className="grid-3" style={{ gap: 10 }}>
                    <Metric label={t.dentists.profit} value={formatMinor(data.owner.profitMinor, data.currency, locale)} />
                    <Metric label={t.dentists.drawn} value={formatMinor(data.owner.drawnMinor, data.currency, locale)} />
                    <Metric label={t.dentists.available} value={formatMinor(data.owner.availableMinor, data.currency, locale)} />
                  </div>
                  <dl className="detail-list" style={{ marginTop: 10 }}>
                    <div><dt>{t.finance.collected}</dt><dd style={{ margin: 0 }} className="ltr-value">{formatMinor(data.owner.collectedMinor, data.currency, locale)}</dd></div>
                    <div><dt>{t.dentists.lessOthers}</dt><dd style={{ margin: 0 }} className="ltr-value">−{formatMinor(data.owner.othersCommissionMinor, data.currency, locale)}</dd></div>
                    <div><dt>{t.dentists.lessLab}</dt><dd style={{ margin: 0 }} className="ltr-value">−{formatMinor(data.owner.labCostMinor, data.currency, locale)}</dd></div>
                    <div><dt>{t.dentists.lessPurchases}</dt><dd style={{ margin: 0 }} className="ltr-value">−{formatMinor(data.owner.purchasesMinor, data.currency, locale)}</dd></div>
                    <div><dt>{t.dentists.lessExpenses}</dt><dd style={{ margin: 0 }} className="ltr-value">−{formatMinor(data.owner.expensesMinor, data.currency, locale)}</dd></div>
                  </dl>
                </div>
              ) : (
              <div>
                <div className="section-label">
                  {salaryOnly ? t.dentists.salaryOwed : t.dentists.commissionOwed}
                </div>
                <div className="grid-3" style={{ marginTop: 8, gap: 10 }}>
                  <Metric
                    label={t.dentists.accrued}
                    value={formatMinor(data.commission.accruedMinor, data.currency, locale)}
                  />
                  <Metric
                    label={t.dentists.paid}
                    value={formatMinor(data.commission.settledMinor, data.currency, locale)}
                  />
                  <Metric
                    label={t.dentists.unsettled}
                    value={formatMinor(data.commission.outstandingMinor, data.currency, locale)}
                  />
                </div>
                {data.salary.accruedMinor > 0 ? (
                  <p style={{ color: 'var(--ink-3)', fontSize: 12.5, marginTop: 8 }}>
                    {t.dentists.salary}: {formatMinor(data.salary.accruedMinor, data.currency, locale)} ·{' '}
                    {t.dentists.salaryNote}
                  </p>
                ) : null}
              </div>
              )}

              <div>
                <div className="section-label">{t.dentists.accrued}</div>
                {data.accruals.length === 0 ? (
                  <p style={{ color: 'var(--ink-3)', marginTop: 6 }}>{t.dentists.noAccruals}</p>
                ) : (
                  <div className="table-scroll" style={{ marginTop: 8 }}>
                    <table>
                      <tbody>
                        {data.accruals.slice(0, 20).map((a) => {
                          const remaining = a.amountMinor - a.allocatedMinor;
                          return (
                            <tr key={a.id}>
                              <td style={{ paddingInlineStart: 0 }}>
                                {formatDate(a.at)}
                                <span className="sub">
                                  {a.sourceType === 'salary'
                                    ? t.dentists.salary
                                    : `${(a.rateBp / 100).toFixed(2)}%`}
                                </span>
                              </td>
                              <td className="ltr-value">{formatMinor(a.amountMinor, data.currency, locale)}</td>
                              <td style={{ paddingInlineEnd: 0, textAlign: 'end' }}>
                                {/* Part-paid is its own state. Rounding it to
                                    "settled" or "unsettled" would misstate the
                                    balance in one direction or the other. */}
                                {a.allocatedMinor === 0 ? (
                                  <span className="badge warn">{t.dentists.unsettled}</span>
                                ) : remaining <= 0 ? (
                                  <span className="badge ok">{t.dentists.settled}</span>
                                ) : (
                                  <span className="badge info">
                                    {t.dentists.partPaid} · {formatMinor(remaining, data.currency, locale)}{' '}
                                    {t.dentists.remaining}
                                  </span>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </>
          ) : null}

          {tab === 'monthly' ? (
            <MonthlyTable data={monthly} currency={data.currency} />
          ) : null}

          {tab === 'agreements' ? (
            <div>
              <p className="hint" style={{ color: 'var(--ink-3)', fontSize: 12, margin: '0 0 10px' }}>
                {t.dentists.frozenNote}
              </p>
              <div className="table-scroll">
                <table>
                  <tbody>
                    {data.agreements.map((a) => (
                      <tr key={a.id}>
                        <td style={{ paddingInlineStart: 0 }}>
                          {(t.dentists.basisKind as Record<string, string>)[a.basis] ?? a.basis}
                        </td>
                        <td className="ltr-value">
                          {a.basis === 'salary'
                            ? `${formatMinor(a.monthlySalaryMinor, a.currency, locale)} / ${t.dentists.perMonth}`
                            : `${(a.rateBp / 100).toFixed(2)}%`}
                        </td>
                        <td>
                          {formatDate(a.effectiveFrom)}
                          {a.effectiveTo ? ` → ${formatDate(a.effectiveTo)}` : ''}
                        </td>
                        <td style={{ paddingInlineEnd: 0, textAlign: 'end' }}>
                          {a.deductLabCost ? <span className="badge">{t.dentists.deductLab}</span> : null}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : null}

          {tab === 'payments' ? (
            <div>
              {data.settlements.length === 0 ? (
                <p style={{ color: 'var(--ink-3)' }}>{t.dentists.noPayments}</p>
              ) : (
                <div className="table-scroll">
                  <table>
                    <tbody>
                      {data.settlements.map((s) => (
                        <tr key={s.id} style={{ opacity: s.status === 'void' ? 0.6 : 1 }}>
                          <td style={{ paddingInlineStart: 0 }}>
                            {formatDate(s.settledAt ?? s.periodEnd)}
                            {s.memo ? <span className="sub">{s.memo}</span> : null}
                          </td>
                          <td>
                            <span className={`badge ${s.kind === 'advance' ? 'warn' : ''}`}>
                              {s.kind === 'advance' ? t.dentists.advance : s.kind === 'draw' ? t.dentists.draw : t.dentists.paid}
                            </span>
                            {s.status === 'void' ? (
                              <span className="badge danger" style={{ marginInlineStart: 6 }}>
                                {t.dentists.reversed}
                              </span>
                            ) : null}
                          </td>
                          <td>{(t.finance.methods as Record<string, string>)[s.method] ?? s.method}</td>
                          <td className="ltr-value" style={{ textAlign: 'end' }}>
                            {formatMinor(s.amountMinor, data.currency, locale)}
                          </td>
                          <td style={{ paddingInlineEnd: 0, textAlign: 'end', whiteSpace: 'nowrap' }}>
                            {/* A payment made in error is undone HERE, so what
                                it settled is reopened along with the cash. */}
                            {canSettle && s.status === 'posted' ? (
                              <button type="button" className="btn-link danger" onClick={() => setReversing(s)}>
                                {t.dentists.reverse}
                              </button>
                            ) : null}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          ) : null}
        </div>
      )}

      {reversing && data ? (
        <ReverseSettlement
          membershipId={membershipId}
          settlement={reversing}
          currency={data.currency}
          onClose={() => setReversing(null)}
          onDone={() => {
            setReversing(null);
            setNonce((n) => n + 1);
            onChanged();
          }}
        />
      ) : null}

      {dialog === 'agreement' && data ? (
        <NewAgreement
          membershipId={membershipId}
          currency={data.currency}
          onClose={() => setDialog(null)}
          onDone={() => {
            setDialog(null);
            setNonce((n) => n + 1);
          }}
        />
      ) : null}

      {dialog === 'pay' && data ? (
        <PayDentist
          membershipId={membershipId}
          name={data.dentist.name}
          currency={data.currency}
          outstandingMinor={data.commission.outstandingMinor}
          owner={data.owner ? { availableMinor: data.owner.availableMinor } : undefined}
          onClose={() => setDialog(null)}
          onDone={() => {
            setDialog(null);
            setNonce((n) => n + 1);
            onChanged();
          }}
        />
      ) : null}
    </Modal>
  );
}

/**
 * Month by month.
 *
 * Produced, earned and paid are shown as three columns for the same month
 * because they are three different things that rarely agree — work done in
 * March may be collected in April and paid in May, and averaging a running
 * total across months would smooth exactly the difference someone is looking
 * for.
 */
function MonthlyTable({ data, currency }: { data: DentistMonthly | null; currency: string }) {
  const { t, locale, formatNumber } = useI18n();
  if (!data) return <Skeleton rows={5} />;

  const withActivity = data.months.filter(
    (m) => m.procedures > 0 || m.commissionMinor > 0 || m.salaryMinor > 0 || m.paidMinor > 0,
  );
  if (withActivity.length === 0) return <EmptyState title={t.dentists.noAccruals} />;

  const peak = Math.max(...withActivity.map((m) => m.producedMinor), 1);

  return (
    <div>
      <p className="hint" style={{ color: 'var(--ink-3)', fontSize: 12, margin: '0 0 12px' }}>
        {t.dentists.monthlyNote}
      </p>

      {/* Drawn from the same rows as the table below, not a second source. */}
      <div className="month-grid" style={{ marginBottom: 16 }}>
        {withActivity.map((m) => (
          <div key={m.month} className="month-col">
            <div className="month-bar" style={{ height: 72 }}>
              <span style={{ height: `${Math.round((m.producedMinor / peak) * 100)}%` }} />
            </div>
            <span className="month-label ltr-value">{m.month.slice(5)}</span>
          </div>
        ))}
      </div>

      <div className="table-scroll">
        <table>
          <thead>
            <tr>
              <th>{t.dentists.month}</th>
              <th>{t.finance.procedures}</th>
              <th>{t.finance.uniquePatients}</th>
              <th>{t.dentists.produced}</th>
              <th>{t.dentists.earned}</th>
              <th>{t.dentists.paid}</th>
            </tr>
          </thead>
          <tbody>
            {withActivity.map((m) => (
              <tr key={m.month}>
                <td className="ltr-value">
                  <strong>{m.month}</strong>
                </td>
                <td>{formatNumber(m.procedures)}</td>
                <td>{formatNumber(m.uniquePatients)}</td>
                <td className="ltr-value">{formatMinor(m.producedMinor, currency, locale)}</td>
                <td className="ltr-value">
                  {formatMinor(m.commissionMinor + m.salaryMinor, currency, locale)}
                  {m.salaryMinor > 0 && m.commissionMinor > 0 ? (
                    <span className="sub">
                      {t.dentists.salary} {formatMinor(m.salaryMinor, currency, locale)}
                    </span>
                  ) : m.salaryMinor > 0 ? (
                    <span className="sub">{t.dentists.salary}</span>
                  ) : null}
                </td>
                <td className="ltr-value">{formatMinor(m.paidMinor, currency, locale)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/**
 * Pay a dentist.
 *
 * The clinic names the amount. "Everything outstanding" is one click, but half
 * of it, or a round hundred, is the same form — because that is what actually
 * happens, and a system that can only pay the exact total forces the clinic to
 * lie to it.
 */
function PayDentist({
  membershipId,
  name,
  currency,
  outstandingMinor,
  owner,
  onClose,
  onDone,
}: {
  membershipId: string;
  name: string;
  currency: string;
  outstandingMinor: number;
  /** Present for the owner: this is a DRAW on profit, with no earned amount to default to. */
  owner?: { availableMinor: number };
  onClose: () => void;
  onDone: () => void;
}) {
  const { t, locale, tx } = useI18n();
  const [mode, setMode] = useState<'full' | 'part'>(owner ? 'part' : 'full');
  const [amount, setAmount] = useState(() => (owner ? '' : minorToInput(Math.max(0, outstandingMinor), currency)));
  // Bank transfer was the default and is no longer one of the methods the
  // clinic operates; paying a dentist follows the same rule as any other
  // payment.
  const [method, setMethod] = useState('cash');
  const [memo, setMemo] = useState('');
  const [allowAdvance, setAllowAdvance] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const amountMinor = owner || mode === 'part' ? parseToMinor(amount, currency) : outstandingMinor;
  // A draw has nothing to overspend against; the available figure is shown,
  // not enforced — the owner may draw ahead of profit on purpose.
  const overspend = !owner && amountMinor !== null && amountMinor > outstandingMinor;

  return (
    <Modal
      title={`${owner ? t.dentists.takeDraw : t.dentists.payDentist} — ${name}`}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose} disabled={busy}>
            {t.patients.cancel}
          </button>
          <button
            type="button"
            className="btn"
            disabled={busy || amountMinor === null || amountMinor <= 0 || (overspend && !allowAdvance)}
            onClick={async () => {
              setBusy(true);
              setError(null);
              try {
                await api.payDentist(membershipId, {
                  amountMinor,
                  method,
                  memo: memo || undefined,
                  allowAdvance: overspend ? true : undefined,
                });
                onDone();
              } catch (e) {
                setError(describe(e, t));
              } finally {
                setBusy(false);
              }
            }}
          >
            {busy ? (
              <Spinner />
            ) : amountMinor !== null ? (
              `${t.dentists.payDentist} — ${formatMinor(amountMinor, currency, locale)}`
            ) : (
              t.dentists.payDentist
            )}
          </button>
        </>
      }
    >
      <div className="stack">
        {error ? (
          <div className="notice danger" role="alert">
            {error}
          </div>
        ) : null}

        <div className="notice">
          <span>{t.dentists.payNote}</span>
        </div>

        <div>
          <div className="section-label">{t.dentists.unsettled}</div>
          <div className="stat-value ltr-value" style={{ fontSize: 24, marginTop: 4 }}>
            {formatMinor(outstandingMinor, currency, locale)}
          </div>
        </div>

        {owner ? (
          <div className="notice">
            <span>
              {t.dentists.drawNote} {t.dentists.available}: <strong className="ltr-value">{formatMinor(owner.availableMinor, currency, locale)}</strong>
            </span>
          </div>
        ) : (
          <div className="segmented" style={{ alignSelf: 'flex-start' }}>
          <button type="button" aria-pressed={mode === 'full'} onClick={() => setMode('full')}>
            {t.dentists.payFull}
          </button>
          <button type="button" aria-pressed={mode === 'part'} onClick={() => setMode('part')}>
            {t.dentists.payPart}
          </button>
        </div>
        )}

        {owner || mode === 'part' ? (
          <Field label={`${owner ? t.dentists.drawAmount : t.dentists.payAmount} (${currency})`} required>
            <input
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              inputMode="decimal"
              className="ltr-value"
              autoFocus
            />
          </Field>
        ) : null}

        {overspend ? (
          <>
            <div className="notice warn">
              <span>{tx(t.dentists.advanceWarning, { name })}</span>
            </div>
            <label className="row" style={{ gap: 8, alignItems: 'flex-start' }}>
              <input
                type="checkbox"
                checked={allowAdvance}
                onChange={(e) => setAllowAdvance(e.target.checked)}
                style={{ marginTop: 3 }}
              />
              <span>{t.dentists.allowAdvance}</span>
            </label>
          </>
        ) : null}

        <div className="grid-2">
          <Field label={t.dentists.payMethod}>
            <select value={method} onChange={(e) => setMethod(e.target.value)}>
              {OFFERED_PAYMENT_METHODS.map((m) => (
                <option key={m.value} value={m.value} disabled={!m.available}>
                  {(t.finance.methods as Record<string, string>)[m.value] ?? m.value}
                  {m.available ? '' : ` — ${t.finance.onHold}`}
                </option>
              ))}
            </select>
          </Field>
          <Field label={t.dentists.payMemo} hint={t.patients.optional}>
            <input value={memo} onChange={(e) => setMemo(e.target.value)} />
          </Field>
        </div>
      </div>
    </Modal>
  );
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <div className="metric">
      <div className="metric-value ltr-value">{value}</div>
      <div className="metric-label">{label}</div>
    </div>
  );
}

/**
 * Reverse a dentist payment made in error. One act: the earnings it paid are
 * owed again, an advance it made is cancelled, the cash comes back into the
 * books, and the payment stays on the statement marked reversed.
 */
function ReverseSettlement({
  membershipId,
  settlement,
  currency,
  onClose,
  onDone,
}: {
  membershipId: string;
  settlement: DentistStatement['settlements'][number];
  currency: string;
  onClose: () => void;
  onDone: () => void;
}) {
  const { t, locale } = useI18n();
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  return (
    <Modal
      title={`${t.dentists.reverseTitle} — ${formatMinor(settlement.amountMinor, currency, locale)}`}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose} disabled={busy}>
            {t.patients.cancel}
          </button>
          <button
            type="button"
            className="btn btn-danger"
            disabled={busy || reason.trim().length < 3}
            onClick={async () => {
              setBusy(true);
              setError(null);
              try {
                await api.reverseSettlement(membershipId, settlement.id, reason.trim());
                onDone();
              } catch (e) {
                setError(describe(e, t));
              } finally {
                setBusy(false);
              }
            }}
          >
            {busy ? <Spinner /> : t.dentists.reverse}
          </button>
        </>
      }
    >
      <div className="stack">
        {error ? (
          <div className="notice danger" role="alert">
            {error}
          </div>
        ) : null}
        <div className="notice">
          <span>{t.dentists.reverseHint}</span>
        </div>
        <Field label={t.dentists.reverseReason} required>
          <input value={reason} onChange={(e) => setReason(e.target.value)} autoFocus />
        </Field>
      </div>
    </Modal>
  );
}

function NewAgreement({
  membershipId,
  currency,
  onClose,
  onDone,
}: {
  membershipId: string;
  currency: string;
  onClose: () => void;
  onDone: () => void;
}) {
  const { t } = useI18n();
  const [basis, setBasis] = useState<'collected' | 'billed' | 'net_of_lab' | 'salary' | 'profit'>('collected');
  const [rate, setRate] = useState('40');
  const [salary, setSalary] = useState('');
  const [deductLab, setDeductLab] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const isSalary = basis === 'salary';
  const isProfit = basis === 'profit';
  const salaryMinor = parseToMinor(salary, currency);

  return (
    <Modal
      title={t.dentists.newAgreement}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose} disabled={busy}>
            {t.patients.cancel}
          </button>
          <button
            type="button"
            className="btn"
            disabled={busy || (isSalary && (salaryMinor === null || salaryMinor <= 0))}
            onClick={async () => {
              setBusy(true);
              setError(null);
              try {
                await api.createAgreement(membershipId, {
                  basis,
                  // The owner takes the profit, not a rate; the server refuses one.
                  ratePercent: isSalary || isProfit ? 0 : Number(rate),
                  monthlySalaryMinor: isSalary ? salaryMinor : 0,
                  currency,
                  deductLabCost: isSalary || isProfit ? false : deductLab,
                });
                onDone();
              } catch (e) {
                setError(describe(e, t));
              } finally {
                setBusy(false);
              }
            }}
          >
            {busy ? <Spinner /> : t.chart.save}
          </button>
        </>
      }
    >
      <div className="stack">
        {error ? (
          <div className="notice danger" role="alert">
            {error}
          </div>
        ) : null}

        <div className="notice">
          <span>{isSalary ? t.dentists.salaryNote : t.dentists.frozenNote}</span>
        </div>

        <Field
          label={t.dentists.basis}
          hint={(t.dentists.basisExplainer as Record<string, string>)[basis]}
          required
        >
          <select value={basis} onChange={(e) => setBasis(e.target.value as typeof basis)}>
            {(['collected', 'billed', 'net_of_lab', 'salary', 'profit'] as const).map((b) => (
              <option key={b} value={b}>
                {(t.dentists.basisKind as Record<string, string>)[b]}
              </option>
            ))}
          </select>
        </Field>

        {isSalary ? (
          <Field label={`${t.dentists.monthlySalary} (${currency})`} required>
            <input value={salary} onChange={(e) => setSalary(e.target.value)} className="ltr-value" inputMode="decimal" autoFocus />
          </Field>
        ) : isProfit ? null : (
          <>
            <Field label={`${t.dentists.rate} (%)`} required>
              <input value={rate} onChange={(e) => setRate(e.target.value)} className="ltr-value" inputMode="decimal" />
            </Field>
            <label className="row" style={{ gap: 8 }}>
              <input type="checkbox" checked={deductLab} onChange={(e) => setDeductLab(e.target.checked)} />
              <span>{t.dentists.deductLab}</span>
            </label>
          </>
        )}
      </div>
    </Modal>
  );
}


/** A dentist who will not sign in: a name and, if wanted, a title. */
function AddDentist({ onClose, onAdded }: { onClose: () => void; onAdded: () => void }) {
  const { t } = useI18n();
  const [fullName, setFullName] = useState('');
  const [title, setTitle] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  async function save() {
    setBusy(true);
    setError(null);
    try {
      await api.addDentist({ fullName: fullName.trim(), publicTitle: title.trim() || null });
      onAdded();
    } catch (e) {
      setError(describe(e, t));
    } finally {
      setBusy(false);
    }
  }
  return (
    <Modal
      title={t.dentists.addTitle}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose} disabled={busy}>
            {t.patients.cancel}
          </button>
          <button type="button" className="btn" onClick={() => void save()} disabled={busy || fullName.trim().length < 2}>
            {busy ? <Spinner /> : t.dentists.add}
          </button>
        </>
      }
    >
      <div className="stack">
        <p className="hint" style={{ margin: 0 }}>{t.dentists.addHint}</p>
        {error ? <div className="notice danger" role="alert">{error}</div> : null}
        <Field label={t.dentists.fullName} required>
          <input value={fullName} onChange={(e) => setFullName(e.target.value)} autoFocus />
        </Field>
        <Field label={t.dentists.titleLabel}>
          <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder={t.dentists.titlePlaceholder} />
        </Field>
      </div>
    </Modal>
  );
}

type Period = { start: string; end: string };

/**
 * A dentist's week: each day off, or one or two periods (a split shift).
 * The week starts on Saturday in Arabic, as Syrian clinics count it, and on
 * Sunday otherwise; the stored weekday is always 0 = Sunday … 6 = Saturday.
 */
function WorkingHours({ membershipId, name, onClose, onSaved }: { membershipId: string; name: string; onClose: () => void; onSaved: () => void }) {
  const { t, tx, locale } = useI18n();
  const [days, setDays] = useState<Period[][] | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const order = locale === 'ar' ? [6, 0, 1, 2, 3, 4, 5] : [0, 1, 2, 3, 4, 5, 6];
  const dayName = (wd: number) => new Intl.DateTimeFormat(locale === 'ar' ? 'ar' : 'en', { weekday: 'long', timeZone: 'UTC' }).format(new Date(Date.UTC(2026, 0, 4 + wd)));

  useEffect(() => {
    api
      .dentistHours(membershipId)
      .then((r) => {
        const week: Period[][] = [[], [], [], [], [], [], []];
        for (const h of r.hours) week[h.weekday]!.push({ start: h.start, end: h.end });
        setDays(week);
      })
      .catch((e) => setError(describe(e, t)));
  }, [membershipId, t]);

  const set = (wd: number, periods: Period[]) => setDays((d) => (d ? d.map((p, i) => (i === wd ? periods : p)) : d));

  async function save() {
    if (!days) return;
    setBusy(true);
    setError(null);
    try {
      await api.setDentistHours(membershipId, days.flatMap((periods, weekday) => periods.map((p) => ({ weekday, start: p.start, end: p.end }))));
      onSaved();
    } catch (e) {
      setError(describe(e, t));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      title={tx(t.dentists.hoursTitle, { name })}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose} disabled={busy}>
            {t.patients.cancel}
          </button>
          <button type="button" className="btn" onClick={() => void save()} disabled={busy || !days}>
            {busy ? <Spinner /> : t.settings.save}
          </button>
        </>
      }
    >
      <div className="stack">
        <p className="hint" style={{ margin: 0 }}>{t.dentists.hoursHint}</p>
        {error ? <div className="notice danger" role="alert">{error}</div> : null}
        {days === null ? (
          <Skeleton rows={7} />
        ) : (
          <div className="hours-grid">
            {order.map((wd) => {
              const periods = days[wd]!;
              const on = periods.length > 0;
              return (
                <div key={wd} className={on ? 'hours-row' : 'hours-row is-off'}>
                  <label className="hours-day">
                    <input type="checkbox" checked={on} onChange={(e) => set(wd, e.target.checked ? [{ start: '09:00', end: '17:00' }] : [])} />
                    <span>{dayName(wd)}</span>
                  </label>
                  <div className="hours-periods">
                    {on ? (
                      periods.map((p, i) => (
                        <div key={i} className="hours-period">
                          <input type="time" aria-label={t.dentists.from} value={p.start} onChange={(e) => set(wd, periods.map((x, j) => (j === i ? { ...x, start: e.target.value } : x)))} className="ltr-value" />
                          <span aria-hidden="true">–</span>
                          <input type="time" aria-label={t.dentists.to} value={p.end} onChange={(e) => set(wd, periods.map((x, j) => (j === i ? { ...x, end: e.target.value } : x)))} className="ltr-value" />
                          {i > 0 ? (
                            <button type="button" className="btn-link danger" onClick={() => set(wd, periods.filter((_, j) => j !== i))}>
                              {t.dentists.removePeriod}
                            </button>
                          ) : periods.length < 2 ? (
                            <button type="button" className="btn-link" onClick={() => set(wd, [...periods, { start: '17:00', end: '20:00' }])}>
                              {t.dentists.addPeriod}
                            </button>
                          ) : null}
                        </div>
                      ))
                    ) : (
                      <span className="hint">{t.dentists.off}</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </Modal>
  );
}
